// Der Kopf des Kontaktdetails und seine Aktionsleiste (Party 360, erster
// Schnitt; ST-ACT-001, Eintrag vom 28.09.2026; CM-ACT-001, NFR-UX-004).
//
// AUF EINEN BLICK, OHNE NEUEN ABRUF: Name, Art, Lebenszyklus, der bevorzugte
// Kontaktweg und die offenen Wiedervorlagen zu diesem Kontakt -- alles aus
// dem Kontakt und der ersten Verlaufsseite, die der Bildschirm ohnehin laedt
// (party-360.ts).
//
// DIE AKTIONSLEISTE ist NAVIGATION und keine Befehlsreihe: echte `<a href>`
// (in neuem Tab oeffnen, Adresse kopieren) in einem `<nav>` mit Namen. Sie
// ersetzt die Pflege-Links, die bis zum 28.09.2026 in der Fusszeile standen
// und nur erreichte, wer bis ganz nach unten scrollte (Experience-Review auf
// 5606502). Die EINE Hauptaktion des Bildschirms bleibt "Notiz festhalten"
// im Verlauf (ADR-0051); hier steht darum kein primary.
//
// "Kontakt bestaetigen" (ST-PTY-001, CM-PTY-008) steht unter dem Kennzeichen
// des Status, solange der Kontakt ein Entwurf ist.
//
// DER NAME ist die <h1> des Bildschirms und steht in dieser Karte (v4): darueber
// der Pfad bis zum Namen, darunter Status-Pille, Art, Haus und Wiedervorlagen. Der
// Rahmen haelt seine eigene <h1> dafuer zurueck (useUeberschriftImInhalt); den
// Namen meldet der Bildschirm weiter fuer den Fenstertitel. Darunter stehen Art
// und Wiedervorlagen, dann die Werte.
//
// DIE SCHNELLAKTIONEN fuehren zu dem, was es schon gibt: "Notiz" ins Notizfeld
// des Verlaufs (ueber den Bildschirm, der dafuer das Register waehlt),
// "Wiedervorlage" in die Auswahl der Wiedervorlage im selben Kasten (v4), und
// "kopieren" legt Name, E-Mail oder Telefonnummer in die Zwischenablage
// (CM-UX-007). Keine neue Handlung, kein neuer Abruf; die EINE Hauptaktion
// bleibt "Notiz festhalten" im Verlauf, darum ist keiner der Knoepfe primary.
//
// DER HINWEIS ZUM ENTWURF (StatusAktion) steht in einer eigenen Zeile unter
// der Kopfkarte, damit die Karte ruhig bleibt und der Hinweis nicht in ihr
// verschwindet (Slice 4b, Entwurf v4).
//
// "IN DER SAMMLUNG" steht in der Aktionsreihe vor "…" (v4), nur wenn der
// Kontakt in einer Sammlung ist; "Zur Sammlung …" bleibt im Menue.

import { useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

import { followUpShort, followUpSummary, type FollowUpTally } from '../../party-360.ts';
import { kindLabel, type PartyDetail, visibilityLabel } from '../../parties.ts';
import { Brotkrume } from '../../shell/brotkrume.tsx';
import { MehrKnopf } from '../../shell/mehr-knopf.tsx';
import { StatusPille } from '../../shell/status-pille.tsx';
import { AppLink } from '../../shell/router.tsx';
import { UEBERSCHRIFT_KOPF } from '../../shell/masse.ts';
import { TypSymbol } from '../../shell/typ-symbol.tsx';
import { FreigabeAktion } from './freigabe-dialog.tsx';
import { StatusAktion } from './status-aktion.tsx';
import { DEACTIVATE_LABEL, notWritableNotice } from '../../party-lifecycle.ts';
import { SHARE_LABEL, shareOffered } from '../../party-share.ts';
import { collectionHref } from '../../collections.ts';
import { ZurSammlungKnopf } from '../zur-sammlung.tsx';
import { InDerSammlung } from './in-der-sammlung.tsx';
import { mitUmbruchstellen, verlinkbar } from './kontaktwege-abschnitt.tsx';
import { mailtoAdresse, telAdresse } from '../../contact-points.ts';
import type { SperrenFuer } from './sperren-abschnitt.tsx';

/** Ein Symbol im 24er-Raster; stumm, der Name steht am Knopf oder daneben. */
function Zeichen({ gross, children }: { readonly gross?: boolean; readonly children: ReactNode }): ReactElement {
  const groesse = gross === true ? 24 : 16;
  return (
    <svg viewBox="0 0 24 24" width={groesse} height={groesse} aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

const BRIEF = <path d="M3.5 6h17v12h-17zM3.5 7l8.5 6.5L20.5 7" />;
const HOERER = <path d="M6.5 3.5h3l1.5 4-2 1.5a11 11 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2 2A15.5 15.5 0 0 1 4.5 5.5a2 2 0 0 1 2-2z" />;
const UHR = (
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7v5l3 2" />
  </>
);
const NOTIZ = <path d="M5 4h14v16H5zM8.5 9h7M8.5 13h7M8.5 17h4" />;
const KOPIEREN = (
  <>
    <rect x="8.5" y="8.5" width="11" height="12" rx="1.5" />
    <path d="M15.5 8.5v-3a1.5 1.5 0 0 0-1.5-1.5H6A1.5 1.5 0 0 0 4.5 5.5v10A1.5 1.5 0 0 0 6 17h2.5" />
  </>
);
const MEHR = (
  <>
    <circle cx="5.5" cy="12" r="1.2" />
    <circle cx="12" cy="12" r="1.2" />
    <circle cx="18.5" cy="12" r="1.2" />
  </>
);



export const useKopfStile = makeStyles({
  // Kopf als Karte auf dem Papier, wie jedes Panel darunter (Entwurf v4).
  karte: {
    padding: '1.25rem',
    boxShadow: tokens.shadow4,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '0.75rem 1rem',
  },
  // 56 px Flaeche in der Markenflaeche (v4 brand-soft, avatar lg), Zeichen 28 px.
  symbol: {
    width: '3.5rem',
    height: '3.5rem',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  inhalt: {
    // 14 rem: neben dem 48-px-Symbol bleibt der Inhalt bei 390 px in einer Reihe.
    flex: '1 1 14rem',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  aktionen: {
    display: 'flex',
    flexWrap: 'wrap',
    // Einzeilig erst ab 80 rem Fenster: daneben braucht der Name noch Platz, und die
    // Navigation nimmt 240 px (Experience-Review K2; Behaelterabfrage siehe stile.ts).
    '@media (min-width: 80rem)': { flexWrap: 'nowrap' },
    alignItems: 'center',
    gap: '0.5rem',
  },
  pfadreihe: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '0 0.75rem',
  },
  // Satz und "Sammlung öffnen" im Textfluss einer Zeile: der Link bricht wie
  // Text um statt als Block (gemessen nie schlechter als eine Flex-Reihe).
  oeffnen: { whiteSpace: 'nowrap' },
  name: {
    margin: 0,
    fontSize: UEBERSCHRIFT_KOPF,
    lineHeight: '1.75rem',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflowWrap: 'anywhere',
  },
  // v4 meta: Pille, Art, Haus und Wiedervorlagen in einer umbrechenden Reihe.
  zeile: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.25rem 0.75rem',
    color: tokens.colorNeutralForeground2,
  },
  fakt: {
    color: tokens.colorNeutralForeground2,
    overflowWrap: 'anywhere',
  },
  nurVorgelesen: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  // Wer den Kontakt sieht, als stiller Text der Metareihe (v4: das Haus).
  sichtbarkeit: {
    color: tokens.colorNeutralForeground3,
    overflowWrap: 'anywhere',
  },
  wert: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    overflowWrap: 'anywhere',
    minWidth: 0,
  },
  werte: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    // 16 px zwischen E-Mail und Telefon: mit Rinne und Sammlungshinweis bleibt
    // die Zeile bei 1280 px einzeilig (24 px brachen sie um 3 px um).
    gap: '0.25rem 1rem',
  },
  // Rechte Spalte der Kopfkarte: Aktionen und Ansage. Schmal bricht sie
  // unter den Inhalt um und steht dann linksbuendig.
  rechts: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '0.5rem',
    flexBasis: '100%',
    // Breit nimmt die Spalte, was ihre Zeilen brauchen, und wird nicht gestaucht:
    // die Aktionsreihe in einer Zeile (Review #385). Die Pflegewege stehen an den
    // Abschnitten (register.tsx).
    '@media (min-width: 80rem)': { flex: '0 0 auto' },
  },
});

export function KontaktKopf({
  party,
  tally,
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  onParty,
  onZumAbschnitt,
  onNotiz,
  onWiedervorlage,
  gesperrt,
}: {
  readonly party: PartyDetail;
  readonly tally: FollowUpTally | undefined;
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly onParty: (party: PartyDetail) => void;
  readonly onZumAbschnitt: (abschnitt: 'name') => void;
  readonly onNotiz: () => void;
  /** Fuehrt in die Auswahl "Wiedervorlage" des Notizkastens (v4). */
  readonly onWiedervorlage: () => void;
  /** Die Sperrlage aus dem Datenpanel; ohne sie stehen E-Mail und Telefon als Text (fail-closed). */
  readonly gesperrt?: SperrenFuer | undefined;
}): ReactElement {
  const own = useKopfStile();
  const [grundOffen, setGrundOffen] = useState(false);
  const [freigabeOffen, setFreigabeOffen] = useState(false);
  const [zurSammlungOffen, setZurSammlungOffen] = useState(false);
  /** Nach "Zur Sammlung …": der Weg zur Sammlung neben dem Satz, nicht in der Region. */
  const [sammlungLink, setSammlungLink] = useState<string | undefined>(undefined);
  /** Steigt nach jedem Lauf mit Schreibversuch: "In der Sammlung" laedt dann neu. */
  const [sammlungsLadung, setSammlungsLadung] = useState(0);
  const ausloeserRef = useRef<HTMLButtonElement | null>(null);
  const deaktivierbar = party.lifecycleStatus === 'draft' || party.lifecycleStatus === 'active';
  // Nicht beschreibbar: kein Weg, den der Server mit 409 party_not_writable ablehnte (Probe B73).
  const nurLesen = notWritableNotice(party.lifecycleStatus) !== undefined;
  const [ansage, setAnsageText] = useState('');
  // Erst leeren, dann setzen: dieselbe Meldung zweimal ("Name kopiert.") wird
  // sonst nicht erneut vorgelesen (WCAG 4.1.3). Der Weg zur Sammlung kommt mit
  // dem Satz, nicht vor ihm.
  const setAnsage = (text: string, sammlung?: string): void => {
    setSammlungLink(undefined);
    setAnsageText('');
    window.setTimeout(() => {
      setAnsageText(text);
      setSammlungLink(sammlung);
    }, 50);
  };
  const email = party.emailAddresses.find((weg) => weg.isPreferred) ?? party.emailAddresses[0];
  const telefon = party.phoneNumbers.find((weg) => weg.isPreferred) ?? party.phoneNumbers[0];
  const kopieren = (wert: string, was: string): void => {
    // Die Zwischenablage kann fehlen (unsicherer Kontext, verweigert): dann
    // sagt der Kopf es, statt still nichts zu tun (NFR-UX-005).
    void Promise.resolve()
      .then(() => navigator.clipboard.writeText(wert))
      .then(
        () => setAnsage(`${was} kopiert.`),
        () => setAnsage(`${was} ließ sich nicht kopieren. Markiere den Text und kopiere ihn von Hand.`),
      );
  };
  const wiedervorlagen = followUpSummary(tally);
  const vollerName =
    party.person !== undefined && (party.person.givenName !== undefined || party.person.familyName !== undefined)
      ? [party.person.givenName, party.person.familyName].filter((teil) => teil !== undefined).join(' ')
      : undefined;
  // Der Name steht als <h1> in dieser Karte: hier nur, was er nicht sagt.
  const zusatzName = vollerName !== undefined && vollerName !== party.displayName ? vollerName : undefined;
  const organisation =
    party.organization === undefined
      ? undefined
      : party.organization.tradeName !== undefined
        ? `${party.organization.legalName} — Handelsname: ${party.organization.tradeName}`
        : party.organization.legalName !== party.displayName
          ? party.organization.legalName
          : undefined;
  return (
    <>
      <Card className={own.karte}>
        <span className={own.symbol}>
          <TypSymbol organisation={party.kind === 'organization'} groesse={28} />
        </span>
        <div className={own.inhalt}>
          <div className={own.pfadreihe}>
            <Brotkrume schritte={[{ label: 'Kontakte', to: '/kontakte' }]} hier={party.displayName} />
          </div>
          <h1 className={own.name}>{party.displayName}</h1>
          <div className={own.zeile}>
            <StatusPille status={party.lifecycleStatus} />
            {/* Sichtbar traegt das Typsymbol die Art (v4); vorgelesen wird sie als Wort. */}
            <span className={own.nurVorgelesen}>{kindLabel(party.kind)}</span>
            {/* Wer den Kontakt sieht (ADR-0064), als Text; der Hausname wird nie
                gekuerzt. Ohne Angabe in der Antwort (aelteres Image) steht nichts
                statt einer geratenen Aussage. */}
            {party.visibility === undefined ? undefined : (
              <Body1 className={own.sichtbarkeit}>{visibilityLabel(party.visibility)}</Body1>
            )}
            {wiedervorlagen === undefined ? undefined : (
              <Body1 className={mergeClasses(own.fakt, own.wert)}>
                <Zeichen>{UHR}</Zeichen>
                {/* Sichtbar kurz wie v4, damit die Metareihe einzeilig bleibt; vorgelesen
                    der ganze Satz mit dem Bezug auf das eigene Haus. */}
                <span aria-hidden="true">{followUpShort(tally)}</span>
                <span className={own.nurVorgelesen}>{wiedervorlagen}</span>
              </Body1>
            )}
          </div>
          {zusatzName === undefined ? undefined : <Caption1 className={own.fakt}>{zusatzName}</Caption1>}
          {organisation === undefined ? undefined : <Caption1 className={own.fakt}>{organisation}</Caption1>}
          <div className={own.werte}>
          {/* Dieselbe Regel wie unter "Kontaktwege" (Nachtrag DESIGN 19.10. V5): mailto/tel nur
              bei geladenen Sperren ohne Treffer, sonst Text; eine Sperre kurz hinter dem Wert. */}
          {[
            { weg: email, art: 'email' as const, zeichen: BRIEF, nomen: 'E-Mail', was: 'E-Mail-Adresse' },
            { weg: telefon, art: 'phone' as const, zeichen: HOERER, nomen: 'Telefonnummer', was: 'Telefonnummer' },
          ].map(({ weg, art, zeichen, nomen, was }) => {
            if (weg === undefined) return undefined;
            const lage = gesperrt?.(art, weg.contactPointId);
            const ziel = art === 'email' ? mailtoAdresse(weg.value) : telAdresse(weg.value);
            const text = mitUmbruchstellen(weg.value, art === 'email');
            // Kurzform hinter dem Wert; der Mittelpunkt wird nicht vorgelesen. Während des Ladens nichts.
            const zusatz =
              lage === undefined ? undefined : lage.treffer.length > 0 ? 'gesperrt' : lage.unvollstaendig ? 'Sperren unvollständig' : undefined;
            return (
              <div key={art} className={own.wert}>
                <Zeichen>{zeichen}</Zeichen>
                <Body1>
                  {verlinkbar(lage) ? <a href={ziel}>{text}</a> : text}
                  {zusatz === undefined ? undefined : (
                    <>
                      <span aria-hidden="true"> · </span>
                      <span className={own.nurVorgelesen}>, </span>
                      {zusatz}
                    </>
                  )}
                </Body1>
                <Button
                  appearance="subtle"
                  size="small"
                  aria-label={`${nomen} kopieren: ${weg.value}`}
                  icon={<Zeichen>{KOPIEREN}</Zeichen>}
                  onClick={() => kopieren(weg.value, was)}
                />
              </div>
            );
          })}
          </div>
        </div>
        <div className={own.rechts}>
        <div className={own.aktionen}>
          {/* Beide secondary: die Hauptaktion des Bildschirms ist "Notiz
              festhalten" im Verlauf, des Entwurfs "Kontakt bestaetigen". */}
          {nurLesen ? undefined : (
            <>
              {/* Sichtbar kurz wie in v4; der zugaengliche Name beginnt mit dem
                  sichtbaren Wort (WCAG 2.5.3) und sagt, was geschieht. */}
              <Button appearance="secondary" icon={<Zeichen>{NOTIZ}</Zeichen>} aria-label="Notiz schreiben" onClick={onNotiz}>
                Notiz
              </Button>
              <Button
                appearance="secondary"
                icon={<Zeichen>{UHR}</Zeichen>}
                aria-label="Wiedervorlage anlegen"
                onClick={onWiedervorlage}
              >
                Wiedervorlage
              </Button>
            </>
          )}
          <InDerSammlung partyId={party.partyId} ladung={sammlungsLadung} apiBaseUrl={apiBaseUrl} accessToken={accessToken} />
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <MehrKnopf
                ref={(knoten: HTMLButtonElement | null) => {
                  ausloeserRef.current = knoten;
                }}
                appearance="subtle" icon={<Zeichen>{MEHR}</Zeichen>} />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => kopieren(party.displayName, 'Name')}>Name kopieren</MenuItem>
                {/* An jedem sichtbaren Kontakt: eine Sammlung aendert ihn nicht, das
                    Hinzufuegen prueft nur die Sichtbarkeit (ADR-0065 Ziffer 4). Ein
                    eigener Knopf kostete im ersten Bildschirm 40 px (B65). */}
                <MenuItem onClick={() => setZurSammlungOffen(true)}>Zur Sammlung …</MenuItem>
                {/* Die folgenreichen Eintraege stehen zusammen unter dem Trenner. Die
                    Freigabe nur, wo der Server sie annehmen koennte; ob die Rolle
                    darf, entscheidet die Policy (403 mit eigenem Satz). */}
                {shareOffered(party) || (deaktivierbar && !grundOffen) ? <MenuDivider /> : undefined}
                {shareOffered(party) ? <MenuItem onClick={() => setFreigabeOffen(true)}>{SHARE_LABEL}</MenuItem> : undefined}
                {deaktivierbar && !grundOffen ? <MenuItem onClick={() => setGrundOffen(true)}>{DEACTIVATE_LABEL}</MenuItem> : undefined}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
        {/* Die Statusregion bleibt immer im DOM, sonst gehen Ansagen verloren; der
            Link steht ausserhalb, wird nicht mit angesagt und ist ein eigener Tabstopp. */}
        <Caption1>
          <span role="status" aria-live="polite" aria-label="Status: Schnellaktionen">
            {ansage}
          </span>
          {sammlungLink !== undefined ? (
            <>
              {' '}
              <AppLink to={collectionHref(sammlungLink)} className={own.oeffnen}>
                Sammlung öffnen
              </AppLink>
            </>
          ) : undefined}
        </Caption1>
        <ZurSammlungKnopf
          apiBaseUrl={apiBaseUrl}
          accessToken={accessToken}
          onReauthenticate={onReauthenticate}
          suche={{}}
          markiert={[party.partyId]}
          zahl=""
          umfangSatz="Hinzugefügt wird dieser Kontakt."
          extern={{
            offen: zurSammlungOffen,
            onSchliessen: () => {
              setZurSammlungOffen(false);
              // Wie bei "Name kopieren": zurueck auf "Weitere Aktionen".
              setTimeout(() => ausloeserRef.current?.focus(), 0);
            },
            onErgebnis: (satz, collectionId) => setAnsage(satz, collectionId),
            onGeschrieben: () => setSammlungsLadung((stand) => stand + 1),
          }}
        />
        </div>
      </Card>
        <StatusAktion
          key={party.partyId}
          apiBaseUrl={apiBaseUrl}
          accessToken={accessToken}
          onReauthenticate={onReauthenticate}
          party={party}
          onParty={onParty}
          onZumAbschnitt={onZumAbschnitt}
          grundOffen={grundOffen}
          onGrundOffen={setGrundOffen}
          ausloeserRef={ausloeserRef}
        />
        <FreigabeAktion
          key={`freigabe-${party.partyId}`}
          apiBaseUrl={apiBaseUrl}
          accessToken={accessToken}
          onReauthenticate={onReauthenticate}
          party={party}
          onParty={onParty}
          offen={freigabeOffen}
          onOffen={setFreigabeOffen}
          ausloeserRef={ausloeserRef}
        />
    </>
  );
}
